import React, { useCallback, useRef, useState } from "react";
import { Stack, Typography } from "@mui/material";
import BottomButton from "../BottomButton";
import TextField from "../TextField";
import PasswordField from "../PasswordField";
import Account from "../../api/Account";
import Brand from "../../api/Brand";
import Color from "../../modules/Color";
import OnboardingShell from "../OnboardingShell";
import { getAppLanguage } from "../../utils/languageUtils";
import { getEnvironment } from "../../utils/getEnvironment";
import AddressField from "../AddressField";
import { FONTS, FORM_LABEL_SX } from "../../constants/styles";

const environment = getEnvironment();

const AffiliateSignup = () => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    // Validation
    const [nameTaken, setNameTaken] = useState(false);
    const [addressTaken, setAddressTaken] = useState(false);

    const redirectToPartnerDashboard = (uuid: string) => {
        if (!uuid) return;
        const currentLanguage = getAppLanguage();
        const redirectUrl = `${environment.admin_address}/redirect/${uuid}?lang=${currentLanguage}`;
        window.location.href = redirectUrl;
    };

    const emailInputRef = useRef<HTMLInputElement>(null);
    const addressInputRef = useRef<HTMLInputElement>(null);
    const passwordInputRef = useRef<HTMLInputElement>(null);

    const validateName = useCallback(async () => {
        if (!name.trim()) return;
        try {
            const res = await Brand.validate({ name: name.trim() });
            setNameTaken(res.nameTaken ?? false);
        } catch (err) {
            console.error("Name validation failed:", err);
        }
    }, [name]);

    const validateAddress = useCallback(async () => {
        if (!address.trim()) return;
        try {
            const res = await Brand.validate({ address: address.trim() });
            setAddressTaken(res.addressTaken ?? false);
        } catch (err) {
            console.error("Address validation failed:", err);
        }
    }, [address]);

    const createBrand = async (uuid: string) => {
        await Brand.register(
            uuid,
            name,
            address,
            new Color(139, 105, 105),
            "",
            "a6d894d4-1493-42bd-a860-bf3ebab41370",
            undefined,
            ""
        );
    };

    const handleSignup = async () => {
        if (
            !name.trim() ||
            !email.trim() ||
            !password.trim() ||
            !address.trim()
        ) {
            alert("Please fill in all fields.");
            return;
        }
        setLoading(true);
        try {
            const uuid = await Account.signUp(name, email, password);
            if (!uuid) return alert("Email is already used.");
            await createBrand(uuid);
            redirectToPartnerDashboard(uuid);
        } catch (err) {
            console.error(err);
            alert("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <OnboardingShell
            variant="form"
            heroTitle="Partner Registration"
            heroSubtitle="Join our affiliate program"
            bottomButton={
                <BottomButton
                    onClick={handleSignup}
                    color="primary"
                    disabled={
                        !name ||
                        !email ||
                        !password ||
                        !address ||
                        nameTaken ||
                        addressTaken ||
                        loading
                    }
                    sticky
                >
                    Register
                </BottomButton>
            }
        >
            <Stack
                className="force-dynamic-translation"
                spacing={1.5}
                alignItems="center"
                sx={{ width: "100%" }}
            >
                <Typography
                    sx={{
                        fontFamily: FONTS.display,
                        fontWeight: 700,
                        fontSize: "22px",
                        textAlign: "center",
                    }}
                >
                    Become a Jemloc Affiliate
                </Typography>
                <Stack spacing={1} width="100%">
                    <Typography sx={FORM_LABEL_SX}>Name</Typography>
                    <TextField
                        placeholder="Personal or company name"
                        value={name}
                        onChange={(e) => setName(e.currentTarget.value)}
                        onBlur={validateName}
                        error={nameTaken}
                        helperText={
                            nameTaken
                                ? "This name is already in use"
                                : undefined
                        }
                        fullWidth
                        nextRef={addressInputRef}
                    />
                    <Typography sx={FORM_LABEL_SX}>Affiliate code</Typography>
                    <AddressField
                        placeholder="Preferrable affiliate code (e.g. yourname123)"
                        value={address}
                        onChange={setAddress}
                        onBlur={validateAddress}
                        error={addressTaken}
                        helperText={
                            addressTaken
                                ? "This address is already taken"
                                : undefined
                        }
                        fullWidth
                        ref={addressInputRef}
                        nextRef={emailInputRef}
                    />
                    <Typography sx={FORM_LABEL_SX}>Email</Typography>
                    <TextField
                        placeholder="Your email"
                        value={email}
                        onChange={(e) =>
                            setEmail(e.currentTarget.value.toLowerCase())
                        }
                        fullWidth
                        ref={emailInputRef}
                        nextRef={passwordInputRef}
                    />
                    <Typography sx={FORM_LABEL_SX}>Password</Typography>
                    <PasswordField
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.currentTarget.value)}
                        fullWidth
                        ref={passwordInputRef}
                        onEnter={handleSignup}
                    />
                </Stack>
            </Stack>
        </OnboardingShell>
    );
};

export default AffiliateSignup;
